









































































































































































































































































































































































































import { Component, Prop, PropSync, Watch, Vue } from "vue-property-decorator";
import store from "@/store";
//  @utilities
import moment from "moment";
import numberHelper from "Utilities/number-helper";
import htmlEncoder from "Utilities/htmlencode-helper";
// @types
import { Company } from "@/types/company";
import { Feature } from "@/types/feature";
import { ExtendedVessel } from "@/types/vessel";
//  @clients
import featuresClient from "Clients/features-client";
import vesselsAdministrationsClient from "Clients/vessels-administrations-client";
//  @modules
import { getModule } from "vuex-module-decorators";
import CompaniesModule from "@/store/clients/Companies.module";
import VesselsModule from "@/store/clients/Vessels.module";
import SnackbarModule from "@/store/clients/Snackbar.module";

const Companies = getModule(CompaniesModule, store);
const Vessels = getModule(VesselsModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({
  components: {},
})
export default class VesselDataModal extends Vue {
  @PropSync("value", { required: true, type: Boolean, default: false }) isModalActive!: boolean;
  @Prop({ type: Array, default: () => [], required: true }) vessels!: any[];
  @PropSync("vesselToEditId", { type: Number, default: null }) syncVesselToEditId!: number | null;
  @Prop({ type: String, default: "848px" }) width!: string;

  // @Data
  isCompanyMenuOpen = false;
  companySearch: string = "";
  internalSelected: Company | null = null;
  featuresList: Feature[] = [];
  activatedFeaturesList: number[] = []; // feature IDs
  initialFeaturesList: number[] = []; // feature IDs that were already on vessel when editing
  formData: Partial<ExtendedVessel> = {};

  // Default features that are always activated for new vessels and cannot be changed
  readonly defaultFeatureIds = [6, 7, 8, 9, 10, 13]; // LoggingHistory, Reports, Notes, Documents, LongTrend, Dashboard

  // @Watchers
  @Watch("isModalActive")
  onModalActiveChanged(isOpen: boolean): void {
    if (isOpen) this.initializeForm();
  }

  @Watch("formData.imoNumber")
  onImoNumberChanged(): void {
    // Re-validate company field when IMO changes
    this.$nextTick(() => {
      const companyField = (this.$refs as any).companyField;
      if (companyField) {
        companyField.resetValidation();
        this.$nextTick(() => {
          companyField.validate();
        });
      }
    });
  }

  //  @Validations
  fieldRequired = {
    required: (value: any) => {
      // Allow 0 as valid value for numeric fields
      if (value === 0) return true;
      // Check if value exists (not null, undefined, or empty string)
      return !!value || "Field is required";
    },
    validateImoNumber: (value: any): string | boolean => {
      // allow required() to handle empty
      if (value === null || value === undefined || String(value).trim() === "") return true;
      if (!/^\d{7}$/.test(String(value))) return "Must be a 7 digit number";

      const imo = Number(value);

      // If company is selected, check for duplicate in that company
      if (this.selectedCompany) {
        const duplicate = this.vessels.find(v => v.imoNumber === imo && v.companyName === this.selectedCompany!.name && v.id !== this.formData.id);
        if (duplicate) return "IMO number exists in this company";
      }

      return true;
    },
    draft: (value: any) => {
      if (value === null || value === undefined || String(value).trim() === "") return true;
      // normalize and parse number (allow comma as decimal separator)
      const num = Number(String(value).replace(",", "."));
      if (!Number.isFinite(num)) return "Must be a number";
      // validate range 0..25 inclusive
      return num >= 0 && num <= 25 ? true : "Must be between 0 and 25";
    },
    nameMaxLength: (value: any) => {
      // allow required() to handle empty - here validate length when present
      if (value === null || value === undefined || String(value).trim() === "") return true;
      return String(value).length <= 30 || "May not be longer than 30 characters";
    },
    // Number of shafts validator: integer between 0 and 2
    shaftsRange: (value: any) => {
      if (value === null || value === undefined || String(value).trim() === "") return true;
      const num = Number(String(value).replace(",", "."));
      if (!Number.isFinite(num) || !Number.isInteger(num)) return "Must be a number";
      return num >= 0 && num <= 2 ? true : "Must be between 0 and 2";
    },
  };

  // @Getters
  get isNewVessel(): boolean {
    return this.syncVesselToEditId == null;
  }

  get ModalMode(): any {
    return {
      mode: this.isNewVessel ? "add" : "edit",
      title: this.isNewVessel ? "Add Vessel" : "Edit Vessel",
      btnText: this.isNewVessel ? "Add Vessel" : "Update",
    };
  }

  get vesselsByCompany(): Partial<ExtendedVessel>[] {
    if (this.selectedCompany == null || this.selectedCompany.id == null) {
      // unassigned vessels
      return this.vessels.filter(vessel => vessel.companyName == null || vessel.companyName === "");
    } else {
      return this.vessels.filter(vessel => vessel.companyName === this.selectedCompany!.name);
    }
  }

  get companies(): Company[] {
    return Companies.companies;
  }

  get filteredCompanies(): Company[] {
    if (!this.companySearch) return this.companies;
    const q = this.companySearch.toLowerCase();
    return this.companies.filter(c => (c.name || "").toLowerCase().includes(q));
  }

  get isCompanySelectAvailable(): boolean {
    return this.companies.length > 1;
  }

  get selectedCompany(): Company | null {
    if (!this.internalSelected) return null;
    return this.internalSelected;
  }

  get isSubmitDisabled(): boolean {
    // Use bound formData properties (template uses formData.*)
    const name = this.formData.name ?? "";
    const imo = this.formData.imoNumber ?? "";
    const draft = this.formData.draftMean ?? "";
    const powerValue = this.formData.maxContinuousRating ?? "";
    const shaftValue = this.formData.speedMaxContinuousRating ?? "";
    const shafts = this.formData.numberOfShafts ?? "";

    const v = this.fieldRequired;

    // validator returns true (valid) or an error string -> treat only true as valid
    const ok = (validator: Function, value: any) => {
      try {
        return validator(value) === true;
      } catch {
        return false;
      }
    };

    const nameOk = ok(v.required, name) && ok(v.nameMaxLength, name);
    const imoOk = ok(v.required, imo) && ok(v.validateImoNumber, imo);
    const draftOk = ok(v.required, draft) && ok(v.draft, draft);
    // power and shaft are required only
    const powerOk = ok(v.required, powerValue);
    const shaftOk = ok(v.required, shaftValue);
    const shaftsOk = ok(v.required, shafts) && ok(v.shaftsRange, shafts);
    // check company IMO rule (no duplicate IMO in selected company)
    const companyImoOk = this.companyImoRule() === true;

    return !(nameOk && imoOk && draftOk && powerOk && shaftOk && shaftsOk && companyImoOk);
  }

  // @Methods
  // Initialize form data based on whether we're creating or editing
  initializeForm(): void {
    if (this.isNewVessel) {
      // New vessel: reset form and pre-select default features
      this.formData = {};
      // Initialize numeric fields to 0 to prevent validation errors
      this.formData = {
        draftMean: 0,
        maxContinuousRating: 0,
        speedMaxContinuousRating: 0,
        numberOfShafts: 0,
      };
      this.internalSelected = null;
      this.activatedFeaturesList = [...this.defaultFeatureIds];
      this.initialFeaturesList = [];

      // Reset all field validations for add mode
      this.$nextTick(() => {
        const form = this.$el.querySelector(".dfi__form");
        if (form) {
          const textFields = form.querySelectorAll(".v-text-field");
          textFields.forEach((field: any) => {
            if (field.__vue__ && field.__vue__.resetValidation) {
              field.__vue__.resetValidation();
            }
          });
        }
      });
    } else {
      // Edit mode: populate form with vessel data
      const vessel = this.vessels.find(v => v.id === this.syncVesselToEditId);
      if (vessel) {
        // Clone vessel data and convert null numeric fields to empty 0 to prevent validation errors
        this.formData = {
          ...vessel,
          speedMaxContinuousRating: vessel.speedMaxContinuousRating ?? 0,
          maxContinuousRating: vessel.maxContinuousRating ?? 0,
          numberOfShafts: vessel.numberOfShafts ?? 0,
          draftMean: vessel.draftMean ?? 0,
        };

        // Set selected company if vessel is assigned
        if (vessel.companyName) {
          const company = this.companies.find(c => c.name === vessel.companyName);
          if (company) {
            this.internalSelected = company;
          }
        } else {
          this.internalSelected = null;
        }

        // Extract feature IDs from vessel.features
        const vesselFeatureIds = vessel.features ? vessel.features.map((f: Feature) => f.id) : [];
        this.activatedFeaturesList = [...vesselFeatureIds];
        this.initialFeaturesList = [...vesselFeatureIds]; // Remember initial features
      }
    }
  }

  close(): void {
    this.isModalActive = false;
    this.$emit("update:vesselToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  cancel(): void {
    this.isModalActive = false;
    // Reset vesselToEditId in parent when canceling
    this.$emit("update:vesselToEditId", null);
    // Also reset form when canceling
    setTimeout(() => {
      this.resetForm();
    }, 300);
  }

  async submit(): Promise<void> {
    try {
      let vessel: ExtendedVessel;

      if (this.isNewVessel) {
        // Create new vessel
        this.formData.features = []; // Clear features to avoid conflicts
        vessel = await vesselsAdministrationsClient.createVessel(this.formData as ExtendedVessel);

        // Assign company after creation if selected
        if (this.selectedCompany != null && this.selectedCompany.id != null) {
          await vesselsAdministrationsClient.assign(vessel.id, this.selectedCompany.id);
        }

        //  Set as a data receiver if formData.isDataReceiver is true
        if (this.formData.isDataReceiver) {
          await vesselsAdministrationsClient.setAsDataReceiver(vessel.id);
        }
      } else {
        // Update existing vessel
        const originalVessel = this.vessels.find(v => v.id === this.syncVesselToEditId);
        const hadCompany = originalVessel?.companyName != null && originalVessel?.companyName !== "";
        const hasCompanyNow = this.selectedCompany != null && this.selectedCompany.id != null;
        const companyChanged = originalVessel?.companyName !== this.selectedCompany?.name;

        // If vessel had no company initially but now has one, assign first then update
        if (!hadCompany && hasCompanyNow) {
          await vesselsAdministrationsClient.assign(this.formData.id, this.selectedCompany!.id);
          vessel = await vesselsAdministrationsClient.update(this.formData as ExtendedVessel);
        }
        // If vessel had a company but now doesn't, update first then unassign
        else if (hadCompany && !hasCompanyNow) {
          vessel = await vesselsAdministrationsClient.update(this.formData as ExtendedVessel);
          await vesselsAdministrationsClient.unassign(this.formData as ExtendedVessel);
        }
        // If company changed (but both had and have company), unassign then assign new
        else if (hadCompany && hasCompanyNow && companyChanged) {
          await vesselsAdministrationsClient.unassign(this.formData as ExtendedVessel);
          await vesselsAdministrationsClient.assign(this.formData.id, this.selectedCompany!.id);
          vessel = await vesselsAdministrationsClient.update(this.formData as ExtendedVessel);
        }
        // Otherwise just update vessel data (company unchanged or both null)
        else {
          vessel = await vesselsAdministrationsClient.update(this.formData as ExtendedVessel);
        }

        // Handle data receiver toggle for existing vessel
        const wasDataReceiver = originalVessel?.isDataReceiver === true;
        const isDataReceiverNow = this.formData.isDataReceiver === true;

        if (!wasDataReceiver && isDataReceiverNow) {
          // Was not data receiver, now it is -> set as data receiver
          await vesselsAdministrationsClient.setAsDataReceiver(vessel.id);
        } else if (wasDataReceiver && !isDataReceiverNow) {
          // Was data receiver, now it's not -> unset as data receiver
          await vesselsAdministrationsClient.unsetAsDataReceiver(vessel.id);
        }
      }

      //  Activate/Deactivate features
      if (this.isNewVessel) {
        // For new vessels: only activate non-default features (default features activated automatically by backend)
        const featuresToActivate = this.activatedFeaturesList.filter(id => !this.defaultFeatureIds.includes(id));
        for (const featureId of featuresToActivate) {
          await vesselsAdministrationsClient.activateFeature(vessel.id, featureId);
        }
      } else {
        // For existing vessels: activate newly selected features and deactivate unchecked ones

        // Features to activate: now selected but were not active before
        const featuresToActivate = this.activatedFeaturesList.filter(id => !this.initialFeaturesList.includes(id));
        for (const featureId of featuresToActivate) {
          await vesselsAdministrationsClient.activateFeature(vessel.id, featureId);
        }

        // Features to deactivate: were active before but now unchecked
        const featuresToDeactivate = this.initialFeaturesList.filter(id => !this.activatedFeaturesList.includes(id));
        for (const featureId of featuresToDeactivate) {
          await vesselsAdministrationsClient.deactivateFeature(vessel.id, featureId);
        }
      }

      this.$emit("onSubmit", { vesselData: vessel, assigned: Boolean(this.selectedCompany), isNew: this.isNewVessel });
      Snackbar.showSnackbar({
        text: `Vessel <b>${htmlEncoder.htmlEncode(this.formData.name)}</b> successfully ${this.isNewVessel ? "created" : "updated"}`,
        color: "success",
      });
    } catch (error) {
      Snackbar.showSnackbar({ text: `Failed to ${this.isNewVessel ? "create" : "update"} vessel` });
    } finally {
      this.$emit("update:vesselToEditId", null);
      // Reset form first, then close modal
      this.resetForm();
      // Small delay to ensure reset completes before closing
      await this.$nextTick();
      this.isModalActive = false;
    }
  }

  // Reset form data and validations
  resetForm(): void {
    // Reset form data
    this.formData = {};
    this.internalSelected = null;
    this.companySearch = "";
    this.activatedFeaturesList = [];
    this.initialFeaturesList = [];

    // Reset all field validations
    this.$nextTick(() => {
      this.$nextTick(() => {
        const imoField = this.$refs.imoField as any;
        const companyField = (this.$refs as any).companyField;

        if (imoField) imoField.resetValidation();
        if (companyField) companyField.resetValidation();

        // Reset all v-text-field validations in the form
        const form = this.$el.querySelector(".dfi__form");
        if (form) {
          const textFields = form.querySelectorAll(".v-text-field");
          textFields.forEach((field: any) => {
            if (field.__vue__ && field.__vue__.resetValidation) {
              field.__vue__.resetValidation();
            }
          });
        }
      });
    });
  }

  // Check if a feature is a default feature (always active for new vessels)
  isDefaultFeature(featureId: number): boolean {
    return this.defaultFeatureIds.includes(featureId);
  }

  numericFilter(event: any): any {
    return numberHelper.numericRoundFilter(event);
  }
  numericDecimalFilter(event: any): any {
    return numberHelper.numericFilter(event);
  }

  selectCompany(company: Company): void {
    this.internalSelected = company;
    this.isCompanyMenuOpen = false;
    this.companySearch = "";
    this.$nextTick(() => {
      // Validate both IMO field and company field after selection
      const imoField = this.$refs.imoField as any;
      const companyField = (this.$refs as any).companyField;

      if (imoField && this.formData.imoNumber) {
        imoField.validate();
      }

      if (companyField) {
        companyField.validate();
      }
    });
  }

  companyImoRule(): string | boolean {
    // only validate if both company and IMO are set
    if (!this.selectedCompany || !this.formData.imoNumber) return true;

    const imo = this.formData.imoNumber;
    const selectedCompanyName = this.selectedCompany.name;

    // Check if any vessel in the selected company has this IMO
    // (excluding the current vessel being edited)
    const duplicate = this.vessels.find(v => v.imoNumber === imo && v.companyName === selectedCompanyName && v.id !== this.formData.id);
    return duplicate ? "A vessel with this IMO number exists in this company" : true;
  }

  async created(): Promise<void> {
    Companies.refreshCompanies();
    this.featuresList = await featuresClient.getAll();
  }
}
